import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { SERVICES } from "@/lib/services";
import { sendPresence, savedEndpoint } from "@/lib/push";
import { takePushPending } from "@/lib/pushLink";

const slugFromKey = (key) => Object.keys(SERVICES).find((s) => SERVICES[s].key === key) || key;

// Ponte Web Push per l'Account Lavoratore. Renderizza null. Gestisce:
// - heartbeat di presenza (non inviare push a un dispositivo attivo);
// - risoluzione del click su notifica riusando lo STESSO percorso del pulsante "Apri servizio",
//   con rivalidazione del suggerimento tramite /agenda/today.
export function PushBridge() {
  const navigate = useNavigate();
  const busy = useRef(false);

  // Presenza: solo se il dispositivo è iscritto alle notifiche.
  useEffect(() => {
    const beat = () => { if (savedEndpoint()) sendPresence(document.visibilityState === "visible"); };
    beat();
    const onVis = () => beat();
    document.addEventListener("visibilitychange", onVis);
    const t = setInterval(() => { if (document.visibilityState === "visible") beat(); }, 60000);
    return () => { document.removeEventListener("visibilitychange", onVis); clearInterval(t); };
  }, []);

  const resolve = async (p) => {
    if (!p || !p.slot_id || busy.current) return;
    busy.current = true;
    try {
      const { data } = await api.get("/agenda/today");
      const slot = (data.slots || []).find((s) => s.id === p.slot_id);
      const stillValid = p.kind === "out"
        ? (data.open_active && slot && slot.status === "in_corso")
        : (slot && data.suggested_id === p.slot_id);
      if (!slot || !stillValid) {
        toast.message("Suggerimento non più disponibile", {
          description: "La timbratura suggerita non è più attiva.",
        });
        navigate("/lavoratore");
        return;
      }
      // Stesso identico percorso del pulsante "Apri servizio".
      const qp = ["open=1"];
      if (slot.entity_kind) qp.push(`kind=${slot.entity_kind}`);
      if (slot.role_or_task) qp.push(`role=${encodeURIComponent(slot.role_or_task)}`);
      if (slot.start_time) qp.push(`start=${encodeURIComponent(slot.start_time)}`);
      if (data.date) qp.push(`date=${encodeURIComponent(data.date)}`);
      navigate(`/registro/${slugFromKey(slot.service_type)}/${slot.entity_id}?${qp.join("&")}`);
    } catch (_) {
      navigate("/lavoratore");
    } finally {
      busy.current = false;
    }
  };

  // Click su notifica mentre l'app è già aperta (postMessage dal Service Worker).
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    const onMsg = (e) => {
      if (e.data?.type === "push-click") {
        const url = e.data.url || "";
        const m = url.match(/[?&]comm=([^&]+)/);
        if (m) {
          const id = decodeURIComponent(m[1]);
          if (window.location.pathname.startsWith("/lavoratore")) {
            window.dispatchEvent(new CustomEvent("orbit:open-comm", { detail: { id } }));
          } else {
            navigate(`/lavoratore?comm=${encodeURIComponent(id)}`);
          }
          return;
        }
        resolve({ slot_id: e.data.slot_id, kind: e.data.kind, date: e.data.date });
      }
    };
    navigator.serviceWorker.addEventListener("message", onMsg);
    return () => navigator.serviceWorker.removeEventListener("message", onMsg);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Click su notifica ad app chiusa / dopo login (riferimento salvato in sessionStorage).
  useEffect(() => {
    const pending = takePushPending();
    if (pending) resolve(pending);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
}
